'use client';

import { Box, Flex, Typography } from '@coolblue-development/freddy';
import { DeliveryCostModal } from '@coolblue-development/next-propositions/client';
import {
  SalesPrice,
  SalesPriceCurrent,
  SalesPriceFormer
} from '@coolblue-development/next-sales-price/client';
import {
  ProductSummary as Product,
  SecondChanceProduct
} from '@features/add-to-cart-page/typings/Product';
import { useTranslation } from '@i18n/useTranslation';

export type Props = {
  product: Product;
  isBusinessCustomer: boolean;
  isAddedAsSubscription: boolean;
  quantity: number;
};

export default function ProductPrice({
  product,
  isBusinessCustomer,
  isAddedAsSubscription,
  quantity
}: Props) {
  const isSubscriptionPrice = !!product.subscription && isAddedAsSubscription;

  if (isSubscriptionPrice) {
    return <SubscriptionProductPrice product={product} />;
  }

  if (product.secondChanceProduct) {
    return (
      <SecondChanceProductPrice
        secondChanceProduct={product.secondChanceProduct}
        isBusinessCustomer={isBusinessCustomer}
        quantity={quantity}
      />
    );
  }

  return (
    <RegularProductPrice
      product={product}
      isBusinessCustomer={isBusinessCustomer}
      quantity={quantity}
    />
  );
}

function SubscriptionProductPrice({ product }: { product: Product }) {
  const { t } = useTranslation('webshop');

  return (
    <Flex direction='column' alignItems='flex-end' flexShrink='0'>
      <Flex>
        <SalesPrice
          size='small'
          currentPrice={
            <SalesPriceCurrent price={product.subscription?.oneTimePaymentAmount ?? 0} />
          }
          additionalLabel={
            <Typography variant='body' component='span' ml={100}>
              {t('addToCart.productSummary.oneTimePayment')}
            </Typography>
          }
        />
      </Flex>

      <Flex>
        <SalesPrice
          size='small'
          currentPrice={
            <SalesPriceCurrent price={product.subscription?.installmentAmount ?? 0} isSubtle />
          }
          additionalLabel={
            <Typography variant='body' component='span' color='subtle' ml={100}>
              {t('addToCart.productSummary.monthlyPayment')}
            </Typography>
          }
        />
      </Flex>

      <DeliveryCostModal productIds={[product.id]} />
    </Flex>
  );
}

function SecondChanceProductPrice({
  secondChanceProduct,
  isBusinessCustomer,
  quantity
}: {
  secondChanceProduct: SecondChanceProduct;
  isBusinessCustomer: boolean;
  quantity: number;
}) {
  if (isBusinessCustomer)
    return (
      <Box flexShrink={0}>
        <SalesPrice
          alignment='right'
          size='small'
          variant='business'
          currentPrice={
            <SalesPriceCurrent
              price={quantity * secondChanceProduct.price.includingVat}
              priceExcludingVat={quantity * (secondChanceProduct.price.excludingVat ?? 0)}
            />
          }
        />
      </Box>
    );

  return (
    <Box flexShrink={0}>
      <SalesPrice
        size='small'
        currentPrice={<SalesPriceCurrent price={secondChanceProduct.price.includingVat} />}
      />
    </Box>
  );
}

function RegularProductPrice({
  product,
  isBusinessCustomer,
  quantity
}: {
  product: Product;
  isBusinessCustomer: boolean;
  quantity: number;
}) {
  const { t } = useTranslation('webshop');

  if (isBusinessCustomer)
    return (
      <Box flexShrink={0}>
        <SalesPrice
          alignment='right'
          size='small'
          variant='business'
          currentPrice={
            <SalesPriceCurrent
              price={quantity * product.priceIncludingVat}
              priceExcludingVat={quantity * (product.priceExcludingVat ?? 0)}
            />
          }
        />
      </Box>
    );

  return (
    <Box flexShrink={0}>
      <SalesPrice
        size='small'
        personalPrice={product.personalPrice}
        currentPrice={<SalesPriceCurrent price={product.priceIncludingVat} />}
        {...(product.listPriceIncludingVat && {
          formerPrice: (
            <SalesPriceFormer
              brandName={product.brand?.name}
              price={product.listPriceIncludingVat}
              {...(!product.subProducts?.length && {
                prefix: t('orderBlock.salesPrice.prefix')
              })}
            />
          )
        })}
      />
    </Box>
  );
}
